import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Popover } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { MomentFormMoment } from "../../../../moment/types/moment-form-moment";
import { useLexicalContext } from "../../lexical-context";
import { CoPilot } from "./co-pilot";
import { useCoPilotToolbarContext } from "./co-pilot-toolbar-context";
import { useCoPilot } from "./use-co-pilot";
import { insert, replaceSelection } from "./utils";

export const CoPilotPanel: FC = () => {
  const { panelState } = useCoPilotToolbarContext();
  if (!panelState) return null;
  return <CoPilotPanelInner />;
};

const CoPilotPanelInner: FC = () => {
  const { panelState, closePanel } = useCoPilotToolbarContext();
  const { filterTags } = useLexicalContext();
  const { control } = useFormContext<MomentFormMoment>();
  useWatch({ control, name: ["id"] });
  const [editor] = useLexicalComposerContext();

  const mergeTags = useMemo(
    () =>
      Object.values(filterTags(""))
        .flatMap((x) => x.groupTags)
        .map((x) => `{{${x.id}}}`),
    [filterTags],
  );

  const coPilot = useCoPilot({
    channel: panelState!.channel,
    mergeTags,
    tone: "professional",
    selection: panelState!.capturedSelection,
  });

  const handleReplace = useCallback(
    (text: string) => {
      replaceSelection(editor, panelState!.format, text);
      closePanel();
    },
    [editor, panelState, closePanel],
  );

  const handleInsert = useCallback(
    (markdown: string) => {
      insert(editor, panelState!.format, markdown);
      closePanel();
    },
    [editor, panelState, closePanel],
  );

  return (
    <Popover
      open={true}
      anchorReference="anchorPosition"
      anchorPosition={panelState!.anchorPosition}
      onClose={closePanel}
      disableScrollLock={true}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <CoPilot
        coPilot={coPilot}
        selection={panelState!.capturedSelection}
        onInsert={handleInsert}
        onReplace={handleReplace}
        maxHeight="850px"
        width="max(500px, 30vw)"
        m={1}
      />
    </Popover>
  );
};
